import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import { useEffect, useState } from "react";
import { Loading } from "@/components/ui/loading";
import { Position, positionService } from "@/services/positionService";
import { PricingBasicForm } from "./components/PricingBasicForm";
import { AdditionalSettingsForm } from "./components/AdditionalSettingsForm";
import { useQuery, useMutation } from "@tanstack/react-query";
import { formSchema } from "./schema";

const EditPricing = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tableName: "",
      employeesRequired: 0,
      billPrice: 0,
      guestsAccommodated: null,
      employeePay: 0,
      allowsMultipleAssignments: false,
      allowsOverlappingEvents: false,
      description: "",
      isTable: false,
      isManager: false,
      isFlatRate: false,
    },
  });

  const { data: position, isLoading } = useQuery({
    queryKey: ["position", id],
    queryFn: async () => {
      if (id === "new") return null;
      return await positionService.getPositionById(id!);
    },
    enabled: id !== "new",
  });

  useEffect(() => {
    if (position) {
      form.reset({
        tableName: position.tableName,
        employeesRequired: position.employeesRequired,
        billPrice: position.billPrice,
        guestsAccommodated: position.guestsAccommodated ?? null,
        employeePay: position.employeePay,
        allowsMultipleAssignments: position.allowsMultipleAssignments,
        description: position.description || "",
        allowsOverlappingEvents: position.allowsOverlappingEvents || false,
        isTable: position.isTable || false,
        requestable: position.requestable || false,
        isManager: position.isManager || false,
        isFlatRate: position.isFlatRate || false,
      });
    }
  }, [position, form]);

  const mutation = useMutation({
    mutationFn: async (values: z.infer<typeof formSchema>) => {
      if (id === "new") {
        return await positionService.createPosition(
          values as Omit<Position, "id">,
        );
      } else {
        return await positionService.updatePosition(id!, values);
      }
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Table successfully updated",
        variant: "success",
      });
      navigate("/admin/pricing");
    },
    onError: (error) => {
      console.error("ERROR SUBMITTING FORM", error);
      toast({
        title: "Error",
        description: "Unable to submit.",
        variant: "destructive",
      });
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate(values);
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="container max-w-2xl mx-auto py-10 px-4">
      <h1 className="text-3xl font-bold text-foreground mb-8">
        {id === "new" ? "Create New Table Type" : "Edit Table Type"}
      </h1>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <PricingBasicForm control={form.control} />
          <AdditionalSettingsForm control={form.control} />

          {loading ? (
            <Loading text="Submitting . . ." />
          ) : (
            <div className="flex gap-4">
              <Button type="submit" className="flex-1">
                Save Changes
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => navigate("/admin/pricing")}
              >
                Cancel
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};

export default EditPricing;
